import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkle, MagicWand, ChatCircleText, ChartLine, CurrencyEur } from "@phosphor-icons/react";

const TOOLS = [
  { key: "content", label: "Générer un post", icon: <MagicWand size={18}/>, fields: [{ name: "topic", label: "Sujet (ex: opération de printemps)", type: "text" }] },
  { key: "description", label: "Réécrire ma description", icon: <Sparkle size={18}/> },
  { key: "review_reply", label: "Répondre à un avis", icon: <ChatCircleText size={18}/>, fields: [{ name: "text", label: "Texte de l'avis", type: "textarea" }, { name: "rating", label: "Note (1-5)", type: "number" }] },
  { key: "visibility", label: "Conseils visibilité", icon: <ChartLine size={18}/> },
  { key: "pricing", label: "Assistant prix indicatif", icon: <CurrencyEur size={18}/>, fields: [{ name: "service", label: "Service à évaluer", type: "text" }] },
];

/**
 * AIToolsPanel — 5 Premium-AI tools (post generator, rewrite, review reply,
 * visibility advice, pricing helper). Owns its inputs/output/loading state
 * so the parent Dashboard doesn't have to track any AI state.
 */
export default function AIToolsPanel({ business, isPremium }) {
  const [inputs, setInputs] = useState({});
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(null);

  const run = async (tool) => {
    if (!isPremium) return toast.error("Premium requis");
    setBusy(tool);
    setOutput("");
    try {
      const r = await api.post("/dashboard/ai", { business_id: business.id, tool, input: inputs });
      setOutput(r.data.output || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur IA");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6" data-testid="ai-tools-panel">
      <h3 className="font-display text-xl font-medium mb-6 flex items-center gap-2">
        <Sparkle size={20} weight="fill" className="text-secondary" /> Outils IA
      </h3>
      {!isPremium && (
        <div className="mb-4 text-xs bg-accent/50 border border-border rounded-sm p-3" data-testid="ai-locked">
          Réservés aux partenaires Premium IA.
        </div>
      )}
      <div className="space-y-2">
        {TOOLS.map((tool) => (
          <details key={tool.key} className="border border-border rounded-sm" data-testid={`ai-tool-${tool.key}`}>
            <summary className="px-3 py-2 text-sm font-semibold cursor-pointer flex items-center gap-2">
              <span className="text-primary">{tool.icon}</span> {tool.label}
            </summary>
            <div className="p-3 border-t border-border space-y-2">
              {(tool.fields || []).map((f) => (
                f.type === "textarea" ? (
                  <textarea
                    key={f.name}
                    placeholder={f.label}
                    value={inputs[f.name] || ""}
                    onChange={(e) => setInputs({ ...inputs, [f.name]: e.target.value })}
                    rows={2}
                    className="w-full border border-border rounded-sm px-2 py-1.5 text-sm bg-background"
                  />
                ) : (
                  <input
                    key={f.name}
                    type={f.type}
                    placeholder={f.label}
                    value={inputs[f.name] || ""}
                    onChange={(e) => setInputs({ ...inputs, [f.name]: e.target.value })}
                    className="w-full border border-border rounded-sm px-2 py-1.5 text-sm bg-background"
                  />
                )
              ))}
              <button
                disabled={!isPremium || busy === tool.key}
                onClick={() => run(tool.key)}
                className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2 rounded-sm text-xs font-semibold disabled:opacity-50"
                data-testid={`ai-run-${tool.key}`}
              >
                {busy === tool.key ? "Génération…" : "Générer"}
              </button>
            </div>
          </details>
        ))}
      </div>
      {output && (
        <div
          className="mt-4 bg-accent/40 border border-border rounded-sm p-4 text-sm whitespace-pre-wrap leading-relaxed"
          data-testid="ai-output"
        >
          {output}
        </div>
      )}
    </div>
  );
}
